import { ArrayTerrainSampler } from "../simulation/terrainSampler.js";
export const MAP_MAGIC = "CARSMAP2";
export const MAP_FORMAT_VERSION = 2;
export const MAP_V1_MAGIC = "CARSMAP1";
export const MAP_V1_FORMAT_VERSION = 1;
export var TrackZone;
(function (TrackZone) {
    TrackZone[TrackZone["PitService"] = 1] = "PitService";
    TrackZone[TrackZone["PitSpeedLimit"] = 2] = "PitSpeedLimit";
})(TrackZone || (TrackZone = {}));
const ZONE_MASK = TrackZone.PitService | TrackZone.PitSpeedLimit;
const RESERVED_MASK = 0xc0;
const HEADER_BYTES = 24;
export function encodeMapArtifact(width, height, terrain, checkpoints, zones = new Uint8Array(width * height)) {
    if (!Number.isInteger(width) ||
        !Number.isInteger(height) ||
        width <= 0 ||
        height <= 0 ||
        terrain.length !== width * height ||
        checkpoints.length !== width * height ||
        zones.length !== width * height)
        throw new Error("Map buffers dimensions do not match");
    if (terrain.some((value) => value > 3) ||
        checkpoints.some((value) => value > 3) ||
        zones.some((value) => value & ~ZONE_MASK))
        throw new Error("Map buffers contain an invalid code");
    const runs = [];
    for (let index = 0; index < terrain.length; index += 1) {
        const code = (terrain[index] ?? 3) |
            ((checkpoints[index] ?? 0) << 2) |
            ((zones[index] ?? 0) << 4);
        const previous = runs[runs.length - 1];
        if (previous && previous.code === code && previous.length < 0xffffffff)
            previous.length += 1;
        else
            runs.push({ code, length: 1 });
    }
    const bytes = new Uint8Array(HEADER_BYTES + runs.length * 5);
    const view = new DataView(bytes.buffer);
    for (let index = 0; index < MAP_MAGIC.length; index += 1)
        bytes[index] = MAP_MAGIC.charCodeAt(index);
    view.setUint8(8, MAP_FORMAT_VERSION);
    view.setUint32(9, width, true);
    view.setUint32(13, height, true);
    view.setUint32(17, runs.length, true);
    let offset = HEADER_BYTES;
    for (const run of runs) {
        view.setUint32(offset, run.length, true);
        view.setUint8(offset + 4, run.code);
        offset += 5;
    }
    return bytes;
}
export function decodeMapArtifact(bytes) {
    if (bytes.length < HEADER_BYTES)
        throw new Error("Invalid map artifact magic");
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const magic = new TextDecoder().decode(bytes.subarray(0, 8));
    const version = view.getUint8(8);
    if (magic !== MAP_MAGIC && magic !== MAP_V1_MAGIC)
        throw new Error("Invalid map artifact magic");
    if (version !== MAP_FORMAT_VERSION &&
        !(magic === MAP_V1_MAGIC && version === MAP_V1_FORMAT_VERSION))
        throw new Error("Unsupported map artifact version");
    const width = view.getUint32(9, true);
    const height = view.getUint32(13, true);
    if (width === 0 || height === 0)
        throw new Error("Map dimensions are empty");
    const runCount = view.getUint32(17, true);
    const terrain = new Uint8Array(width * height);
    const checkpoints = new Uint8Array(width * height);
    const zones = new Uint8Array(width * height);
    let offset = HEADER_BYTES;
    let cursor = 0;
    for (let run = 0; run < runCount; run += 1) {
        if (offset + 5 > bytes.length)
            throw new Error("Truncated map artifact");
        const length = view.getUint32(offset, true);
        const code = view.getUint8(offset + 4);
        const terrainCode = code & 3;
        const checkpointCode = (code >> 2) & 3;
        if (terrainCode > 3 ||
            checkpointCode > 3 ||
            (version === MAP_FORMAT_VERSION && (code & RESERVED_MASK) !== 0))
            throw new Error("Invalid map artifact code");
        if (cursor + length > terrain.length)
            throw new Error("Map artifact run exceeds dimensions");
        terrain.fill(terrainCode, cursor, cursor + length);
        checkpoints.fill(checkpointCode, cursor, cursor + length);
        zones.fill(version === MAP_FORMAT_VERSION ? (code >> 4) & ZONE_MASK : 0, cursor, cursor + length);
        cursor += length;
        offset += 5;
    }
    if (cursor !== terrain.length || offset !== bytes.length)
        throw new Error("Map artifact does not cover dimensions");
    return {
        width,
        height,
        terrain,
        checkpoints,
        zones,
        sampler: new ArrayTerrainSampler(width, height, terrain, checkpoints, zones),
    };
}
/** Explicit historical reader for callers that need to reject v2 assets. */
export function decodeMapArtifactV1(bytes) {
    if (new TextDecoder().decode(bytes.subarray(0, 8)) !== MAP_V1_MAGIC)
        throw new Error("Expected a v1 map artifact");
    return decodeMapArtifact(bytes);
}
